import { Link } from 'react-router'

import type { LandingBlockProps } from '@features/landing-page-builder/model/blockRegistry'
import { resolveLandingAction } from '@features/landing-page-builder/model/resolveTarget'

import { cn } from '@shared/lib/cn'
import Button from '@shared/ui/button'

import styles from './LandingButtonBlock.module.scss'

/*
  Generic CTA. The destination comes from payload.actionId (a shared page action)
  or from the block's own target; a target we cannot route to renders nothing
  rather than a dead button.

  theme = 'link' renders an inline text link (used for outbound social links),
  anything else renders the standard primary button.
*/
interface ButtonPayload {
  label?: string;
  text?: string;
  actionId?: string;
  theme?: string;
}

export default function LandingButtonBlock ({ block, payload, actions }: LandingBlockProps) {
  const { label, text, actionId, theme } = (payload ?? {}) as ButtonPayload

  const caption = label || text
  const target = resolveLandingAction(actions, actionId, block.target)

  if (!caption || !target) {
    return null
  }

  const isLink = theme === 'link'

  return (
    <div className={cn(styles.root, { [styles._link]: isLink })}>
      {isLink ? (
        target.external ? (
          <a
            className={styles.root__link}
            href={target.href}
            target="_blank"
            rel="noreferrer noopener"
          >
            {caption}
          </a>
        ) : (
          <Link className={styles.root__link} to={target.href}>{caption}</Link>
        )
      ) : (
        <Button
          to={target.href}
          size="large"
          asHTMLAnchor={target.external}
          {...(target.external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
        >
          {caption}
        </Button>
      )}
    </div>
  )
}
